import { useState } from "react";
import { Button } from "@component/button";
import { Badge } from "@component/badge";
import {
    AlertCircleIcon,
    InfoIcon,
    MoreHorizontalIcon,
    SearchIcon,
    UserPlusIcon,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import { JobOwnerItem } from "@type/job";
import { JobCreateRequest } from "@service/employer";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { useSnackbar } from "@hook/use-snackbar";
import { removeJobCollaboration, resendInvitationJobCollaboration } from "@service/job";
import { parseFormat } from "@util/date";
import { catchErrorLog } from "@util/sentry";
import DialogMemberJobAccess from "./dialog/dialog-member-job-access";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import { EmptyState } from "@component/empty-state";
import { Tooltip } from "@component/tooltip";
import { useNavigate } from "react-router-dom";

interface AccessControlStepProps {
    data: JobCreateRequest;
    onChange: (data: JobOwnerItem[]) => void;
    isEditMode?: boolean;
}

export default function AccessControlStep({
    data,
    onChange,
    isEditMode,
}: AccessControlStepProps) {
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const [, userInfo] = useUserProfileContext();
    const { subscriptionActive, isWorkspaceRestricted } = useSubscription();
    const isTrialSubscription = !!subscriptionActive?.is_trial;
    // Edit mode keeps collaborator management open through the 7-day grace;
    // create mode locks immediately on trial expiry. Paid plans bypass.
    const isTrialGate = isEditMode
        ? isWorkspaceRestricted
        : !!subscriptionActive?.is_expired;
    const isSubscriptionLimitReached =
        isTrialSubscription && (
            isTrialGate
            || !!subscriptionActive?.is_cv_screening_limit_reached
            || !!subscriptionActive?.is_cv_limit_reached
            || !!subscriptionActive?.is_ai_limit_reached
        );
    const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);

    const updateData = (field: keyof JobCreateRequest, value: any) => {
        const updatedData = { ...data };
        if (field === "listing_owners") {
            updatedData.listing_owners = value;
        }
        onChange(updatedData.listing_owners || []);
    };

    const handleAddMemberClick = () => {
        setShowAddMemberDialog(true);
    };

    const removeJobOwner = (email: string) => {
        console.log('Removing job owner with email:', email);
        const owner = (data.listing_owners || []).find((o) => o.email === email);
        if (!owner) {
            console.error("Owner not found");
            return;
        };
        if (!owner.is_send_invitation) {
            updateData(
                "listing_owners",
                (data.listing_owners || []).filter((o) => o.email !== email)
            );
        } else {
            removeJobCollaboration({
                user_id: owner.user_id || null,
                user_email: owner.email || null,
                job_id: data.id || "",
            }).then((resp) => {
                if (resp.status === 200) {
                    updateData(
                        "listing_owners",
                        (data.listing_owners || []).filter((o) => o.email !== email)
                    );
                    snackbar.showSuccess("Collaborator removed from this job listing.");
                }
            }).catch((err) => {
                const error = err as any;
                if (error.response && error.response.data && error.response.data.message) {
                    snackbar.showError("Error removing collaborator.");
                } else {
                    snackbar.showError("Failed to remove collaborator. Please try again.");
                }
                catchErrorLog(`Failed to remove job collaborator: ${err}`);
            });
        }
    };

    const resendInvitation = (email: string) => {
        const owner = (data.listing_owners || []).find((o) => o.email === email);
        if (!owner) return;

        resendInvitationJobCollaboration({
            job_id: data.id || "",
            email: owner.email,
        }).then((resp) => {
            if (resp) {
                snackbar.showSuccess("Invitation resent successfully.");
            }
        }).catch((err) => {
            const error = err as any;
            if (error.response && error.response.data && error.response.data.message) {
                snackbar.showError("Error resending invitation.");
            } else {
                snackbar.showError("Failed to resend invitation. Please try again.");
            }
            catchErrorLog(`Failed to resend job collaborator invitation: ${err}`);
        });
    };

    (data.listing_owners || []).sort((a, b) => {
        const statusOrder: Record<string, number> = {
            "owner": 5,
            "admin": 4,
            "collaborator": 3,
            "external": 2,
            "invited": 1,
        };
        // if owner.is_external is true, consider it as "external" status
        if (a.is_external) {
            statusOrder[a.status] = 2;
        }
        if (b.is_external) {
            statusOrder[b.status] = 2;
        }
        return (statusOrder[b.status] || 0) - (statusOrder[a.status] || 0);
    });

    return (
        <>
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Access Control
                    </h2>
                    <p className="text-gray-600">
                        Manage collaborators for your job listing.
                    </p>
                </div>

                <div className="space-y-6">
                    {/* Job Owners Section */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="space-y-1">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Job Collaborators
                                </h3>
                            </div>
                            <Button
                                onClick={handleAddMemberClick}
                                disabled={isSubscriptionLimitReached}
                                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                            >
                                <UserPlusIcon className="h-4 w-4" />
                                Add Collaborator
                            </Button>
                        </div>

                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Date Joined
                                    </th>
                                    <th className="px-4 py-3 text-center flex flex-row align-middle justify-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Job Role
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <button className="text-gray-400 hover:text-gray-600 transition-colors">
                                                    <InfoIcon
                                                        className="h-4 w-4"
                                                        style={{ color: "#2563eb" }}
                                                    />
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-80 p-4" align="start">
                                                <div className="space-y-4">
                                                    <h4 className="font-semibold text-sm text-gray-900">
                                                        Job Role Permissions
                                                    </h4>
                                                    <div className="space-y-4">
                                                        <div className="space-y-1">
                                                            <p className="font-medium text-sm text-gray-900">
                                                                Admins
                                                            </p>
                                                            <p className="text-sm text-gray-500 leading-relaxed">
                                                                Have full access to all job actions, including
                                                                editing and shortlisting.
                                                            </p>
                                                        </div>
                                                        <div className="space-y-1">
                                                            <p className="font-medium text-sm text-gray-900">
                                                                Members
                                                            </p>
                                                            <p className="text-sm text-gray-500 leading-relaxed">
                                                                Have view access to job listings and applicants.
                                                            </p>
                                                        </div>
                                                        <div className="space-y-1">
                                                            <p className="font-medium text-sm text-gray-900">
                                                                Collaborators
                                                            </p>
                                                            <p className="text-sm text-gray-500 leading-relaxed">
                                                                Members who are granted shortlisting access for
                                                                this job.
                                                            </p>
                                                        </div>
                                                        <div className="space-y-1">
                                                            <p className="font-medium text-sm text-gray-900">
                                                                External
                                                            </p>
                                                            <p className="text-sm text-gray-500 leading-relaxed">
                                                                Users outside the workspace invited to
                                                                collaborate, with the same access as
                                                                Collaborators.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[250px]">
                                        &nbsp;
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {(data.listing_owners || []).length > 0 ? (
                                    <>
                                        {(data.listing_owners || []).map((owner) => {
                                            const isOwner = owner.status === "owner";
                                            const isCurrentUser = owner.user_id === userInfo?.id || owner.status === "admin";

                                            return (
                                                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {owner.name || "-"}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                                        <span className="truncate">{owner.email}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-center text-gray-500 dark:text-gray-400">
                                                        {owner.status === "owner"
                                                            ? `-`
                                                            : owner.status === "invited" && owner.joined_at
                                                                ? `Invited on ${parseFormat(owner.joined_at, "D MMMM YYYY")}`
                                                                : owner.joined_at
                                                                    ? `Joined on ${parseFormat(owner.joined_at, "D MMMM YYYY")}`
                                                                    : "-"}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm flex items-center justify-center">
                                                        {isOwner ? (
                                                            <Badge
                                                                variant="secondary"
                                                                className="bg-green-100 text-green-800 hover:bg-green-100 shadow-none"
                                                                style={{
                                                                    borderRadius: "0.75rem",
                                                                    paddingTop: "5px",
                                                                    paddingBottom: "5px",
                                                                }}
                                                            >
                                                                {isCurrentUser ? "You (Owner)" : "Owner"}
                                                            </Badge>
                                                        ) : owner.status === "admin" ? (
                                                            <Badge
                                                                variant="secondary"
                                                                onClick={() => {
                                                                    navigate("/setting?tab=user-permissions");
                                                                }}
                                                                className="bg-blue-100 text-blue-800 hover:bg-blue-100 shadow-none flex items-center gap-1.5"
                                                                style={{
                                                                    borderRadius: "0.75rem",
                                                                    paddingTop: "5px",
                                                                    paddingBottom: "5px",
                                                                    paddingLeft: "10px",
                                                                    paddingRight: "10px",
                                                                }}
                                                            >
                                                                Admin
                                                                <Tooltip content={(<p>View Workspace Admins</p>)}>
                                                                    <AlertCircleIcon className="h-3.5 w-3.5 text-blue-600 cursor-pointer" />
                                                                </Tooltip>
                                                            </Badge>
                                                        ) : owner.status === "invited" ? (
                                                            <Badge
                                                                variant="secondary"
                                                                className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 shadow-none"
                                                                style={{
                                                                    borderRadius: "0.75rem",
                                                                    paddingTop: "5px",
                                                                    paddingBottom: "5px",
                                                                }}
                                                            >
                                                                Invited
                                                            </Badge>
                                                        ) : owner.status === "collaborator" ? (
                                                            <Badge
                                                                variant="secondary"
                                                                className="bg-purple-100 text-purple-800 hover:bg-purple-100 shadow-none"
                                                                style={{
                                                                    borderRadius: "0.75rem",
                                                                    paddingTop: "5px",
                                                                    paddingBottom: "5px",
                                                                }}
                                                            >
                                                                Collaborator
                                                            </Badge>
                                                        ) : (owner.status === "external" || owner.is_external) ? (
                                                            <Badge
                                                                variant="secondary"
                                                                className="bg-orange-100 text-orange-800 hover:bg-orange-100 shadow-none"
                                                                style={{
                                                                    borderRadius: "0.75rem",
                                                                    paddingTop: "5px",
                                                                    paddingBottom: "5px",
                                                                }}
                                                            >
                                                                External
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="secondary"
                                                                className="bg-purple-100 text-purple-800 hover:bg-purple-100 shadow-none"
                                                                style={{
                                                                    borderRadius: "0.75rem",
                                                                    paddingTop: "5px",
                                                                    paddingBottom: "5px",
                                                                }}
                                                            >
                                                                Team
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        {(!isCurrentUser && !isOwner) && (
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-8 w-8 p-0"
                                                                    >
                                                                        <MoreHorizontalIcon className="h-4 w-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    {owner.status === "invited" ? (
                                                                        <>
                                                                            {owner.is_send_invitation && (
                                                                                <DropdownMenuItem
                                                                                    onClick={() => resendInvitation(owner.email)}
                                                                                    className="cursor-pointer"
                                                                                >
                                                                                    Resend invitation
                                                                                </DropdownMenuItem>
                                                                            )}
                                                                            <DropdownMenuItem
                                                                                onClick={() => removeJobOwner(owner.email)}
                                                                                className="cursor-pointer text-red-600"
                                                                            >
                                                                                Remove from listing
                                                                            </DropdownMenuItem>
                                                                        </>
                                                                    ) : (
                                                                        <DropdownMenuItem
                                                                            onClick={() => removeJobOwner(owner.email)}
                                                                            className="cursor-pointer text-red-600"
                                                                        >
                                                                            Remove from listing
                                                                        </DropdownMenuItem>
                                                                    )}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                            <EmptyState
                                                icon={(
                                                    <SearchIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                                                )}
                                                title="No Collaborators found."
                                                description={
                                                    <>
                                                        It seems like you haven’t added any collaborators to this job yet.<br />
                                                        Click the “Add Collaborator” button to get started.
                                                    </>
                                                }
                                            />
                                        </td>
                                    </tr>
                                )}

                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Add Member Dialog */}
            <DialogMemberJobAccess
                isOpen={showAddMemberDialog}
                onClose={() => setShowAddMemberDialog(false)}
                list_owner={(data.listing_owners || [])}
                onUpdateData={updateData}
            />
        </>
    );
}
